import {
  Box,
  Checkbox,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled, isRejected } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../../app/hooks";
import { CustomButton } from "../../../../../common/components/functional/customButton/CustomButton";
import DialogComponent from "../../../../../common/components/functional/dialogs/DialogComponent";
import { isoFormat } from "../../../../../common/helpers/formatDateTime";
import { updateContinuousEmploymentRange } from "../../../../talents/talentsSlice";

const OPEN_ENDED_DATE = "+999999999-12-31";
const RANGE_SPLITS_EMPLOYMENT_ERROR =
  "continuous_employment_range_splits_employment";

type Props = {
  open: boolean;
  continuousEmploymentId: string;
  startDate: string;
  endDate?: string | null;
  onClose: () => void;
  onUpdated: () => void;
};

const dateFromIsoDate = (value?: string | null) => {
  if (!value || value.startsWith("+")) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const ContinuousEmploymentRangeDialog = ({
  open,
  continuousEmploymentId,
  startDate,
  endDate,
  onClose,
  onUpdated,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [start, setStart] = useState<Date | null>(dateFromIsoDate(startDate));
  const [end, setEnd] = useState<Date | null>(dateFromIsoDate(endDate));
  const [openEnded, setOpenEnded] = useState(
    !endDate || endDate.startsWith("+"),
  );
  const [splitErrorOpen, setSplitErrorOpen] = useState(false);

  const saveDisabled = !start || (!openEnded && !end);

  const submit = () => {
    if (!start || (!openEnded && !end)) return;

    dispatch(
      updateContinuousEmploymentRange({
        continuousEmploymentId,
        range: {
          start: format(start, isoFormat),
          end: openEnded ? OPEN_ENDED_DATE : format(end as Date, isoFormat),
        },
        locallyHandledErrors: [RANGE_SPLITS_EMPLOYMENT_ERROR],
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setSplitErrorOpen(false);
        onUpdated();
        onClose();
        return;
      }
      if (
        isRejected(action) &&
        action.error.message?.includes(RANGE_SPLITS_EMPLOYMENT_ERROR)
      ) {
        setSplitErrorOpen(true);
      }
    });
  };

  return (
    <>
      <DialogComponent open={open} onClose={onClose} autoWidth>
        <Box display="flex" flexDirection="column" gap={2} minWidth="360px">
          <DialogTitle sx={{ padding: 0 }}>
            <Typography variant="header" fontWeight={600} fontSize={23}>
              {t("talentProfile:continuousEmploymentRange")}
            </Typography>
          </DialogTitle>
          <DesktopDatePicker
            label={t("talentProfile:startDate")}
            value={start}
            onChange={setStart}
            maxDate={!openEnded && end ? end : undefined}
            format="dd.MM.yyyy"
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
              },
            }}
          />
          <DesktopDatePicker
            label={t("talentProfile:endDate")}
            value={end}
            onChange={setEnd}
            minDate={start || undefined}
            disabled={openEnded}
            format="dd.MM.yyyy"
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={openEnded}
                onChange={(_, checked) => setOpenEnded(checked)}
              />
            }
            label={t("talentProfile:openEndedContinuousEmployment")}
          />
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <CustomButton onClick={onClose}>{t("common:cancel")}</CustomButton>
            <CustomButton disabled={saveDisabled} onClick={submit}>
              {t("common:save")}
            </CustomButton>
          </Box>
        </Box>
      </DialogComponent>
      <DialogComponent
        open={splitErrorOpen}
        onClose={() => setSplitErrorOpen(false)}
        autoWidth
        draggable={false}
      >
        <Box display="flex" flexDirection="column" gap={2} minWidth="360px">
          <DialogTitle sx={{ padding: 0 }}>
            <Typography variant="header" fontWeight={600} fontSize={23}>
              {t("talentProfile:continuousEmploymentRange")}
            </Typography>
          </DialogTitle>
          <Typography>
            {t("talentProfile:continuousEmploymentRangeSplitsEmploymentError")}
          </Typography>
          <Box display="flex" justifyContent="flex-end">
            <CustomButton onClick={() => setSplitErrorOpen(false)}>
              {t("common:close")}
            </CustomButton>
          </Box>
        </Box>
      </DialogComponent>
    </>
  );
};
